import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCustomToast} from './custom-toast-context'
import {isCartPathname} from '../utils/gtm-utils'

// Lazy-load the drawer so it is excluded from the initial bundle
const AddToCartConfirmationDrawer = loadable(
    () => import('../components/add-to-cart-confirmation-drawer'),
    {ssr: false}
)

/**
 * Fires the bag-page "Added to cart" toast for a single add and undoes that add
 * on demand. Rendered as a sibling of `children` so the basket it needs for
 * Undo never re-renders the confirmation consumers.
 */
const AddToCartToast = ({request, onDone}) => {
    const intl = useIntl()
    const {showCustomToast, removeToast} = useCustomToast()
    const {data: basket} = useCurrentBasket()
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')

    // Undo reads the basket as of the click, not as of the toast opening.
    const basketRef = useRef(basket)
    basketRef.current = basket

    const hasFiredRef = useRef(false)
    const isUndoingRef = useRef(false)

    useEffect(() => {
        if (hasFiredRef.current) return
        hasFiredRef.current = true

        const productId = request.variant?.productId || request.product?.id
        const addedQuantity = request.quantity || 1

        const toastId = showCustomToast({
            message: intl.formatMessage({
                id: 'cart.info.added_to_cart',
                defaultMessage: 'Added to cart'
            }),
            actionLabel: intl.formatMessage({
                id: 'cart_minicart.button.undo',
                defaultMessage: 'Undo'
            }),
            onActionClick: async () => {
                if (isUndoingRef.current) return
                isUndoingRef.current = true

                const basketId = basketRef.current?.basketId
                const item = basketRef.current?.productItems?.find(
                    (productItem) => productItem.productId === productId
                )

                try {
                    if (!basketId || !item) return
                    // The line may have held units before this add, so only take
                    // back the quantity this add contributed.
                    if (item.quantity > addedQuantity) {
                        await updateItemInBasketMutation.mutateAsync({
                            parameters: {basketId, itemId: item.itemId},
                            body: {
                                productId: item.productId,
                                quantity: item.quantity - addedQuantity
                            }
                        })
                    } else {
                        await removeItemFromBasketMutation.mutateAsync({
                            parameters: {basketId, itemId: item.itemId}
                        })
                    }
                } catch (error) {
                    console.error('The error occured while undoing an add to cart: ', error)
                } finally {
                    removeToast(toastId)
                }
            },
            onClose: () => onDone(request.id)
        })
    }, [])

    return null
}

AddToCartToast.propTypes = {
    request: PropTypes.shape({
        id: PropTypes.number.isRequired,
        product: PropTypes.object,
        variant: PropTypes.object,
        quantity: PropTypes.number
    }).isRequired,
    onDone: PropTypes.func.isRequired
}

export const AddToCartConfirmationContext = createContext({
    isOpen: false,
    addedItem: null,
    openConfirmation: () => {},
    closeConfirmation: () => {},
    setConfirmationTrigger: () => {}
})

export const useAddToCartConfirmation = () => useContext(AddToCartConfirmationContext)

/**
 * Provides the add-to-cart confirmation drawer to the application.
 *
 * Consumers call `openConfirmation({ product, variant, quantity })` after a
 * successful add-to-cart to surface the drawer — except on the bag page, where
 * the drawer would cover the page the shopper is already on and a toast is
 * shown instead.
 */
export const AddToCartConfirmationProvider = ({children}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [addedItem, setAddedItem] = useState(null)
    const [toastRequest, setToastRequest] = useState(null)
    // Stores the DOM element that triggered the quick-view so Chakra can
    // return focus to it (preserving scroll position) when the drawer closes.
    const triggerRef = useRef(null)
    const toastRequestIdRef = useRef(0)

    const setConfirmationTrigger = useCallback((element) => {
        triggerRef.current = element || null
    }, [])

    // Close the drawer whenever the route changes (e.g. user clicks the
    // CMS-driven "Go to Shopping Bag" CTA and navigates to /cart).
    const {pathname} = useLocation()
    const initialPathnameRef = useRef(pathname)
    useEffect(() => {
        if (pathname === initialPathnameRef.current) return
        initialPathnameRef.current = pathname
        setIsOpen(false)
    }, [pathname])

    // Read through a ref so `openConfirmation` stays referentially stable.
    const pathnameRef = useRef(pathname)
    pathnameRef.current = pathname

    const openConfirmation = useCallback(({product, variant, quantity}) => {
        if (isCartPathname(pathnameRef.current)) {
            setToastRequest({id: ++toastRequestIdRef.current, product, variant, quantity})
            return
        }
        setAddedItem({product, variant, quantity})
        setIsOpen(true)
    }, [])

    const closeConfirmation = useCallback(() => {
        setIsOpen(false)
    }, [])

    // Only clear the request the closing toast belongs to, so a second add
    // while the first toast is still up isn't dropped.
    const handleToastDone = useCallback((id) => {
        setToastRequest((current) => (current?.id === id ? null : current))
    }, [])

    return (
        <AddToCartConfirmationContext.Provider
            value={{isOpen, addedItem, openConfirmation, closeConfirmation, setConfirmationTrigger}}
        >
            {children}
            <AddToCartConfirmationDrawer
                isOpen={isOpen}
                onClose={closeConfirmation}
                addedItem={addedItem}
                finalFocusRef={triggerRef}
            />
            {toastRequest && (
                <AddToCartToast
                    key={toastRequest.id}
                    request={toastRequest}
                    onDone={handleToastDone}
                />
            )}
        </AddToCartConfirmationContext.Provider>
    )
}

AddToCartConfirmationProvider.propTypes = {
    children: PropTypes.node.isRequired
}
